/**
 * MBA Lab — cookie consent banner.
 *
 * Two states of one component: a non-modal first-run bar, and a modal preferences panel. All of the
 * decision logic lives in ./consent-state.js; this file owns only the DOM.
 *
 * The bar is deliberately non-modal with no scrim. That is not a style preference: eng/visual's
 * measureNavCollapse clicks .navbar-toggler on every route at mobile and tablet, so a full-viewport
 * scrim at first paint would break the harness site-wide, and with it every real pointer path into
 * the nav. A first-run notice should not be a consent wall either.
 */

import {
  analyticsAllowed,
  clearAnalyticsCookies,
  hasAnalyticsCookie,
  needsPrompt,
  read,
  write,
} from './consent-state.js';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function init() {
  const root = document.querySelector('[data-cc-root]');
  if (!root) return;

  const bar = root.querySelector('[data-cc-bar]');
  const panel = root.querySelector('[data-cc-panel]');
  const scrim = root.querySelector('[data-cc-scrim]');
  const toggle = root.querySelector('[data-cc-analytics-toggle]');
  if (!bar || !panel || !scrim || !toggle) return;

  const html = document.documentElement;
  let lastTrigger = null;

  // --- bar --------------------------------------------------------------------------------

  function showBar() {
    bar.hidden = false;
    html.classList.add('cc-bar-open');
    measureBar();
  }

  function hideBar() {
    bar.hidden = true;
    html.classList.remove('cc-bar-open');
    html.style.removeProperty('--cc-h');
  }

  /**
   * Publishes the bar's height so the tool dock can lift clear of it, and so keyboard-focused page
   * content is not left underneath it (WCAG 2.2 SC 2.4.11). Measured rather than hardcoded because
   * the copy wraps to two lines at 375px and one at 1280px.
   */
  function measureBar() {
    if (bar.hidden) return;
    html.style.setProperty('--cc-h', Math.ceil(bar.getBoundingClientRect().height) + 'px');
  }

  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(measureBar).observe(bar);
  } else {
    window.addEventListener('resize', measureBar);
  }

  // --- panel ------------------------------------------------------------------------------

  function openPanel(trigger) {
    lastTrigger = trigger || null;
    // Reflect the real current state, not a hardcoded default. Rendering this ON with no stored
    // choice would be a pre-ticked consent box (unlawful under Planet49); rendering it OFF would
    // misrepresent a US visitor whose analytics is genuinely running. The presence of _ga is the
    // observable truth and needs no geo lookup.
    const stored = read();
    toggle.checked = stored ? stored.granted : hasAnalyticsCookie();

    scrim.hidden = false;
    panel.hidden = false;
    html.classList.add('cc-modal-open');
    // The bar sits underneath the scrim while the panel is open; make it unreachable rather than
    // merely covered, so focus cannot wander into it.
    bar.inert = true;

    const first = panel.querySelector(FOCUSABLE);
    if (first) first.focus();
  }

  function closePanel() {
    scrim.hidden = true;
    panel.hidden = true;
    html.classList.remove('cc-modal-open');
    bar.inert = false;

    // Closing the panel is never a decision, so if the visitor still has not made one the first-run
    // bar has to come back. Otherwise opening the panel from the footer and pressing Escape would
    // dismiss the prompt for the rest of the page view - consent by inaction, which is exactly what
    // the missing Escape handler on the bar exists to prevent.
    if (needsPrompt()) showBar();

    // Return focus to whatever opened the panel - the bar's own button on first run, or the footer
    // control on a later visit. document.contains is NOT sufficient: it is true for an element
    // inside a hidden subtree, and .focus() on an undisplayed element silently does nothing, which
    // would drop the user back to the top of the document.
    if (lastTrigger && lastTrigger.offsetParent !== null) {
      lastTrigger.focus();
    } else {
      const fallback = bar.hidden ? document.querySelector('[data-cc-open]') : bar.querySelector(FOCUSABLE);
      if (fallback) fallback.focus();
    }
    lastTrigger = null;
  }

  // --- decisions --------------------------------------------------------------------------

  function decide(granted, source) {
    write(granted, source);

    if (granted) {
      // __mbalabLoadGtag is idempotent and early-returns once the tag is loaded - so on a
      // reject-then-accept within one page view it does nothing at all, and without the explicit
      // grant below the tag would stay denied for the rest of the visit. The cookie would say
      // granted, the switch would show on, trackEvent's gtag guard would pass, and GA4 would
      // discard everything. Silent, and only recovered by a navigation.
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { analytics_storage: 'granted' });
      }
      if (typeof window.__mbalabLoadGtag === 'function') window.__mbalabLoadGtag();
    } else {
      // A consent update to denied stops gtag reading and writing, but leaves any identifier
      // already on the device there indefinitely. Withdrawal that leaves the identifier in place
      // is not withdrawal.
      clearAnalyticsCookies();
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
      }
    }

    // Order matters: drop cc-ask BEFORE closePanel, or its needsPrompt() re-check would reopen the
    // bar we are in the middle of dismissing. write() above has already stored the choice, so
    // needsPrompt() is false by now regardless - this is belt and braces.
    html.classList.remove('cc-ask');
    html.classList.add('cc-decided');
    closePanel();
    hideBar();

    document.dispatchEvent(new CustomEvent('mbalab:consentchange', {
      detail: { granted, source },
    }));
  }

  // --- wiring -----------------------------------------------------------------------------

  root.querySelector('[data-cc-accept]').addEventListener('click', () => decide(true, 'bar'));
  root.querySelector('[data-cc-reject]').addEventListener('click', () => decide(false, 'bar'));
  root.querySelector('[data-cc-reject-all]').addEventListener('click', () => decide(false, 'panel'));
  root.querySelector('[data-cc-save]').addEventListener('click', () => decide(toggle.checked, 'panel'));

  root.querySelector('[data-cc-manage]').addEventListener('click', (event) => {
    openPanel(event.currentTarget);
  });

  // Footer control, and anything else that opts in. This is the Art. 7(3) withdrawal path, and it
  // has to be as easy to reach as giving consent was.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('[data-cc-open]');
    if (!opener) return;
    event.preventDefault();
    if (!bar.hidden) hideBar();
    openPanel(opener);
  });

  scrim.addEventListener('click', closePanel);

  document.addEventListener('keydown', (event) => {
    if (panel.hidden) return;

    if (event.key === 'Escape') {
      // Closes without saving, and never counts as a decision. Note there is deliberately no
      // Escape handler on the BAR: dismissing a first-run notice without choosing would be consent
      // by inaction, which is unlawful in the EEA and a deceptive pattern everywhere else.
      event.preventDefault();
      closePanel();
      return;
    }

    if (event.key === 'Tab') {
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      // `inert` on the bar is not a focus trap for the rest of the page, so cycle explicitly.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // --- first run --------------------------------------------------------------------------

  if (needsPrompt()) {
    showBar();
  } else if (analyticsAllowed()) {
    // Idempotent; covers a stored grant the head script could not see.
    if (typeof window.__mbalabLoadGtag === 'function') window.__mbalabLoadGtag();
  } else {
    // Reconciles a stored REJECTION the head script missed, which is a real divergence rather than
    // a theoretical one: the head reads only document.cookie, while consent-state reads the cookie
    // OR its localStorage mirror. Safari's ITP caps script-written cookies at 7 days but leaves
    // localStorage alone, so on day 8 a US visitor who clicked Reject looks unprompted to the head
    // script, which then defaults them to allowed and starts GA.
    //
    // Without this branch that rejection is silently overridden, and the preferences panel still
    // shows the switch correctly off - so nothing looks wrong.
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
    clearAnalyticsCookies();
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

export { init };
